import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import {
  isEmptyTableCellConfig,
  normalizeTableCellConfig,
  parseNumericText,
  type TableCellButton,
  type TableCellConfig,
} from '../../../../chart-components/unified-table-matrix-chart/table-cell-config';
import { CellButtonsComponent, type CellButtonOptions } from './cell-buttons.component';
import { ColumnNavigationComponent } from './column-navigation.component';
import { TableCellConfigEditorComponent } from '../../../components/shared/table-cell-config-editor.component';

/** One column the Table & Matrix widget renders, as the editor lists it. */
export interface TableColumnView {
  key: string;
  label: string;
  /** 'column' = flat table column, 'measure' = drill-down measure header. */
  kind: 'column' | 'measure';
  numeric: boolean;
}

/** Column key → generic cell configuration (what the inspector writes as
 *  `appearance.columnCellConfigs`; resolveWidgetData folds it into the data). */
export type TableColumnCellConfigs = Record<string, TableCellConfig>;

/**
 * Design-tab per-column editor for the Table & Matrix widget (Normal /
 * Editable / Drill-Down) — the counterpart of the nested table's Columns
 * section. Lists the columns the widget ACTUALLY renders (its capabilities,
 * else the sample / bound rows, else the drill-down headers) and hosts the
 * ONE shared cell-configuration editor per column. Writes go to
 * `appearance.columnCellConfigs`; the widget data underneath is never touched.
 */
@Component({
  selector: 'app-design-table-matrix-columns',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatTooltipModule, TableCellConfigEditorComponent, ColumnNavigationComponent, CellButtonsComponent],
  template: `
<p class="tmc-hint" *ngIf="!columns().length">No columns to configure yet — generate the table or pick a table type with sample columns.</p>

<app-design-column-navigation [contextKey]="contextKey()" [columns]="navigationColumns()" (configChange)="setLinkConfig($event.field, $event.config)"></app-design-column-navigation>

<div class="tmc-column" *ngFor="let column of columns(); trackBy: trackColumn" [class.is-set]="isConfigured(column.key)">
  <div class="tmc-row">
    <span class="tmc-label" [title]="column.key">{{ column.label }}</span>
    <span class="tmc-kind" *ngIf="column.kind === 'measure'">measure</span>
    <button type="button" class="tmc-icon" *ngIf="isConfigured(column.key)" (click)="reset(column.key)" matTooltip="Reset this column" aria-label="Reset column">
      <mat-icon>restart_alt</mat-icon>
    </button>
    <label class="tmc-currency" matTooltip="Convert currency: shown × the rate selected in the Currency exchange dropdown">
      <input type="checkbox" [checked]="!!(columnConfig(column.key)?.convertCurrency ?? dataCellConfig(column.key)?.convertCurrency)" (change)="setCurrency(column.key, $any($event.target).checked)"
        [attr.aria-label]="'Convert currency for ' + column.label" />
      <span>$↔</span>
    </label>
    <button type="button" class="tmc-icon" (click)="toggleOpen(column.key)" [attr.aria-expanded]="isOpen(column.key)" aria-label="Column settings">
      <mat-icon>{{ isOpen(column.key) ? 'expand_less' : 'tune' }}</mat-icon>
    </button>
  </div>
  <div class="tmc-details" *ngIf="isOpen(column.key)">
    <app-table-cell-config-editor
      title="Cell formatting" [showNavigation]="false"
      [config]="columnConfig(column.key)"
      [fields]="fieldKeys()"
      [values]="columnValues(column.key)"
      [showEditor]="column.kind === 'column'"
      [valueKind]="column.numeric ? 'number' : 'text'"
      (configChange)="setConfig(column.key, $event)"
    ></app-table-cell-config-editor>
    <app-design-cell-buttons
      [buttons]="columnConfig(column.key)?.buttons ?? []"
      [context]="buttonContext()"
      [tableName]="tableName()"
      (buttonsChange)="setButtons(column.key, $event)"
    ></app-design-cell-buttons>
  </div>
</div>
`,
  styles: [`
    :host { display: block; font-size: 12px; color: #344054; }
    .tmc-hint { margin: 0; color: #667085; }
    .tmc-column { border: 1px solid #eaecf0; border-radius: 6px; margin-top: 6px; background: #fff; }
    .tmc-column.is-set { border-color: rgba(41, 112, 255, .45); }
    .tmc-row { display: flex; align-items: center; gap: 4px; padding: 4px 4px 4px 8px; }
    .tmc-label { flex: 1 1 auto; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: #101828; }
    .tmc-kind { font-size: 10px; color: #98a2b3; text-transform: uppercase; letter-spacing: .04em; }
    .tmc-icon { display: inline-flex; align-items: center; justify-content: center; flex: none; width: 22px; height: 22px; padding: 0; border: 0; border-radius: 4px; background: none; color: #667085; cursor: pointer; }
    .tmc-icon mat-icon { width: 16px; height: 16px; font-size: 16px; }
    .tmc-icon:hover { background: #f2f4f7; color: #2970ff; }
    .tmc-details { padding: 6px 8px 8px; border-top: 1px solid #f2f4f7; }
    .tmc-currency { display: inline-flex; align-items: center; gap: 2px; flex: none; font-size: 10px; color: #667085; cursor: pointer; }
    .tmc-currency input[type='checkbox'] { width: 13px; height: 13px; margin: 0; }
  `],
})
export class TableMatrixColumnsComponent {
  /** The widget data as rendered (capabilities + rows / drill-down headers). */
  readonly contextKey = input('');
  readonly data = input<Record<string, unknown> | null>(null);
  readonly configs = input<TableColumnCellConfigs>({});
  readonly configsChange = output<TableColumnCellConfigs>();
  /** Option lists for the cell buttons' feature settings (null = the editor works them out). */
  readonly buttonContext = input<CellButtonOptions | null>(null);
  readonly tableName = input('');

  private readonly openKeys = signal<ReadonlySet<string>>(new Set());

  readonly columns = computed<TableColumnView[]>(() => tableMatrixColumnsOf(this.data()));
  readonly fieldKeys = computed(() => this.columns().map((column) => column.key));
  /** Column key → distinct values in the widget's rows (for "+ From data"
   *  under Value colours). Memoized per data() so the editor gets a stable list. */
  private readonly valuesByColumn = computed<Record<string, string[]>>(() => {
    const data = this.data() ?? {};
    const rows = (Array.isArray(data['staticData']) ? data['staticData'] : Array.isArray(data['rows']) ? data['rows'] : []) as Array<Record<string, unknown>>;
    const out: Record<string, string[]> = {};
    this.columns().forEach((column) => {
      if (column.numeric) return;
      const seen = new Set<string>();
      rows.forEach((row) => {
        const text = String(row?.[column.key] ?? '').trim();
        if (text && !seen.has(text)) seen.add(text);
      });
      if (seen.size) out[column.key] = Array.from(seen);
    });
    return out;
  });
  columnValues(key: string): string[] | undefined { return this.valuesByColumn()[key]; }

  readonly navigationColumns = computed(() => this.columns().map(column => ({ field: column.key, label: column.label, cellConfig: this.columnConfig(column.key) })));
  columnConfig(key: string): TableCellConfig | undefined {
    if (Object.prototype.hasOwnProperty.call(this.configs(), key)) return this.configs()[key];
    return this.dataCellConfig(key);
  }
  /** The column's own cell configuration from the build (Data tab), ignoring Design overrides. */
  dataCellConfig(key: string): TableCellConfig | undefined {
    const data = this.data() ?? {};
    const caps = (data['capabilities'] ?? {}) as Record<string, unknown>;
    const drill = (data['drilldownData'] ?? caps['drilldownData'] ?? {}) as Record<string, unknown>;
    for (const entries of [caps['columns'], caps['measures'], drill['headers']]) {
      if (!Array.isArray(entries)) continue;
      const match = entries.find(entry => entry && [entry.field, entry.label, entry.prop, entry.fullName, entry.name].includes(key));
      if (match?.control === 'hyperlink') return { ...match.cellConfig, display: 'link', linkTemplate: match.cellConfig?.linkTemplate ?? match.hrefTemplate };
      if (match?.cellConfig) return match.cellConfig;
    }
    return undefined;
  }
  setLinkConfig(key: string, config: TableCellConfig): void {
    const normalized = normalizeTableCellConfig(config) ?? {};
    this.configsChange.emit({ ...this.configs(), [key]: config.display === 'text' ? { ...normalized, display: 'text' } : normalized });
  }

  isOpen(key: string): boolean { return this.openKeys().has(key); }
  toggleOpen(key: string): void {
    this.openKeys.update((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }
  isConfigured(key: string): boolean { return !isEmptyTableCellConfig(this.configs()[key]); }

  /** A column's buttons: with any, the column draws them instead of its value. */
  setButtons(key: string, buttons: TableCellButton[]): void {
    const current = this.columnConfig(key) ?? {};
    const display = buttons.length ? 'buttons' : current.display === 'buttons' ? undefined : current.display;
    this.setConfig(key, { ...current, buttons, display });
  }

  setConfig(key: string, config: TableCellConfig | undefined): void {
    const next: TableColumnCellConfigs = { ...this.configs() };
    const normalized = normalizeTableCellConfig(config);
    const previousDisplay = this.configs()[key]?.display;
    if (!normalized?.display && previousDisplay && previousDisplay !== 'link') {
      next[key] = { ...normalized, display: 'text' };
      this.configsChange.emit(next);
      return;
    }
    if (normalized) next[key] = normalized;
    else delete next[key];
    this.configsChange.emit(next);
  }
  reset(key: string): void { this.setConfig(key, undefined); }

  /** Convert currency: the column follows the Currency exchange rate (display only). */
  setCurrency(key: string, on: boolean): void {
    this.setConfig(key, { ...(this.columnConfig(key) ?? {}), convertCurrency: on });
  }

  trackColumn(_index: number, column: TableColumnView): string { return column.key; }
}

const isNumeric = (value: unknown): boolean =>
  typeof value === 'number' || (typeof value === 'string' && parseNumericText(value) !== null);

/** The columns a Table & Matrix widget renders, from its data. */
/** Distinct non-empty values per column key in the widget's loaded rows (at most 200 each). */
export function tableColumnValuesOf(data: Record<string, unknown> | null | undefined, keys: string[]): Record<string, string[]> {
  const rows = (Array.isArray(data?.['staticData']) ? data!['staticData'] : Array.isArray(data?.['rows']) ? data!['rows'] : []) as Array<Record<string, unknown>>;
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const out: Record<string, string[]> = {};
  keys.forEach((key) => {
    const rowKey = Object.keys(rows[0] ?? {}).find((candidate) => norm(candidate) === norm(key)) ?? key;
    const values = new Set<string>();
    for (const row of rows) {
      const value = row?.[rowKey];
      if (value !== null && value !== undefined && String(value).trim() !== '') values.add(String(value).trim());
      if (values.size >= 200) break;
    }
    out[key] = [...values].sort((a, b) => a.localeCompare(b));
  });
  return out;
}

export function tableMatrixColumnsOf(data: Record<string, unknown> | null | undefined): TableColumnView[] {
  if (!data) return [];
  const caps = (data['capabilities'] ?? {}) as Record<string, unknown>;
  const rows = (Array.isArray(data['staticData']) ? data['staticData'] : Array.isArray(data['rows']) ? data['rows'] : []) as Array<Record<string, unknown>>;
  const sample = rows[0] ?? {};
  const out: TableColumnView[] = [];
  const seen = new Set<string>();
  const push = (view: TableColumnView): void => { if (view.key && !seen.has(view.key)) { seen.add(view.key); out.push(view); } };

  // Drill-Down: the baked headers (non-tree ones) are the measure columns.
  const drill = (data['drilldownData'] ?? caps['drilldownData']) as Record<string, unknown> | undefined;
  const headers = Array.isArray(drill?.['headers']) ? (drill!['headers'] as Array<Record<string, unknown>>) : [];
  if (headers.length) {
    headers.forEach((header) => {
      if (!header || header['expandabile'] || !header['prop']) return;
      const label = String(header['fullName'] ?? header['name'] ?? header['prop']);
      push({ key: String(header['prop']), label, kind: 'measure', numeric: header['type'] !== 'text' });
    });
    return out;
  }
  const measures = Array.isArray(caps['measures']) ? (caps['measures'] as Array<Record<string, unknown>>) : [];
  if (measures.length && (caps['tableType'] === 'drilldown' || caps['dimensions'])) {
    measures.forEach((measure) => {
      const key = String(measure['label'] || measure['field'] || '');
      push({ key, label: key, kind: 'measure', numeric: measure['kind'] !== 'text' });
    });
    return out;
  }

  const configured = Array.isArray(caps['columns']) ? (caps['columns'] as Array<Record<string, unknown>>) : [];
  if (configured.length) {
    configured.forEach((column) => {
      if (!column || column['isAction']) return;
      const field = String(column['field'] ?? '');
      const type = String(column['type'] ?? '');
      const numeric = type ? type !== 'text' && type !== 'date' : isNumeric(sample[field]);
      push({ key: field, label: String(column['label'] || field), kind: 'column', numeric });
    });
    return out;
  }
  Object.keys(sample).forEach((key) => {
    if (key.startsWith('__')) return;
    push({ key, label: key, kind: 'column', numeric: isNumeric(sample[key]) });
  });
  return out;
}
